import {
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  MouseOutlined,
  VisibilityOutlined,
  TouchAppOutlined,
  ShowChartOutlined,
} from '@mui/icons-material';
import { MomentumDeltasTableProps } from '../../types/momentum.types';

const METRIC_CONFIG = {
  clicks: {
    label: 'Clicks',
    icon: <TouchAppOutlined />,
    format: (val: number) => val.toLocaleString(),
  },
  impressions: {
    label: 'Impressions',
    icon: <VisibilityOutlined />,
    format: (val: number) => val.toLocaleString(),
  },
  ctr: {
    label: 'CTR',
    icon: <MouseOutlined />,
    format: (val: number) => `${(val * 100).toFixed(2)}%`,
  },
  position: {
    label: 'Position',
    icon: <ShowChartOutlined />,
    format: (val: number) => val.toFixed(1),
  },
};

export default function MomentumDeltasTable({
  deltas,
  compact = false,
}: MomentumDeltasTableProps) {
  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    if (trend === 'up') return <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />;
    if (trend === 'down') return <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />;
    return <TrendingFlat sx={{ fontSize: 16, color: 'info.main' }} />;
  };

  const getTrendColor = (trend: 'up' | 'down' | 'stable', isPositiveUp: boolean = true) => {
    if (trend === 'stable') return 'default';
    const isGood = isPositiveUp ? trend === 'up' : trend === 'down';
    return isGood ? 'success' : 'error';
  };

  const metrics = Object.entries(deltas) as Array<[keyof typeof deltas, typeof deltas[keyof typeof deltas]]>;

  return (
    <Paper elevation={1} sx={{ overflow: 'hidden' }}>
      {!compact && (
        <Box sx={{ p: 2, pb: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Performance Deltas
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Comparison between current and previous period
          </Typography>
        </Box>
      )}

      <TableContainer>
        <Table size={compact ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Previous</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Current</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Change</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Trend</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {metrics.map(([metricKey, metric]) => {
              const config = METRIC_CONFIG[metricKey];
              const isPositiveUp = metricKey !== 'position'; // Position: lower is better

              return (
                <TableRow key={metricKey} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {config.icon}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {config.label}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="textSecondary">
                      {config.format(metric.previous)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {config.format(metric.current)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${metric.percentChange > 0 ? '+' : ''}${metric.percentChange.toFixed(1)}%`}
                      size="small"
                      color={getTrendColor(metric.trend, isPositiveUp) as any}
                      variant="outlined"
                      sx={{ minWidth: 70 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {getTrendIcon(metric.trend)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="textSecondary">
          ↑ Better • ↓ Worse • → Stable
          {' • '}
          <strong>Position:</strong> Lower is better
        </Typography>
      </Box>
    </Paper>
  );
}
